import React, {
    createContext,
    useContext,
    useState,
    useCallback,
    useMemo,
    useRef
} from 'react';
import getTimestamp from "@dyl/utils/getTimestamp";
import merge from "lodash/merge";
import LoadingScreen from "@dyl/components/LoadingScreen";
import { isEqual } from "lodash";

const TIMEOUT_INTERVAL = process.env.SESSION_STORAGE_TIMEOUT_SECONDS || 300;



/**
 * Creates a System-Pattern provider and hook consumer,
 *  for use with remote/async data.
 * @template T
 * @param {Object} [options]
 * @param {() => Promise<T>} [options.requestFunc]
 * @param {() => () => Promise<T>} [options.requestHook]
 * @param {T} [options.defaultValue]
 * @param {string} [options.contextName]
 */
export default function createDatabaseManager(options) {
    const {
        requestFunc,
        requestHook,
        defaultValue = null,
        contextName = 'context',
        persist = false,
        initialStatus = 'not started'
    } = options ?? {};

    if (!requestFunc && !requestHook) {
        throw new Error('createDatabaseManager must be called with either a requestFunc or requestHook option.');
    }
    else if (requestFunc && requestHook) {
        throw new Error('createDatabaseManager must be called with only one of either a requestFunc or requestHook option.');
    }

    function throwNoContext() { throw new Error(`No ${contextName} in tree`); }

    const defaults = {
        data: defaultValue,
        /** @type {() => Promise<void>} */
        refresh() { throwNoContext(); },
        /** @type {'not started' | 'loading' | 'complete' | 'error'} */
        status: initialStatus,
        /** @type {() => Promise<void>} */
        reset() { throwNoContext(); },
        /** @type {() => Promise<void>} */
        update() { throwNoContext(); },
    };

    const Context = createContext(defaults);

    const createInitialState = () => {
        if (persist) {
            try {
                // Expiry check
                const timestamp = window.sessionStorage.getItem(`${contextName}SetTime`);
                const expired = timestamp + TIMEOUT_INTERVAL < getTimestamp();
                if (!expired) {
                    const savedData = window.sessionStorage.getItem(contextName);
                    console.log(`[${contextName}] Loading session: ${savedData}`);
                    if (savedData) return JSON.parse(savedData);
                } else {
                    window.sessionStorage.removeItem(contextName);
                }
            } catch {}
        }

        return {
            data: defaults.data,
            status: defaults.status,
        };
    }

    function DatabaseManager({ children }) {

        const [isLoading, setIsLoading] = useState(false);
        const [{ data, status }, _setState] = useState(createInitialState);
        const setState = (newState, ...rest) => {
            if (rest.length > 0) throw new Error(rest);
            _setState(newState);
            // The type check is to differentiate status updates from the data updates.
            if (persist && (typeof newState !== 'function')) {
                const jsonStr = JSON.stringify(newState);
                console.log(`[${contextName}] Saving session: ${jsonStr}`);
                window.sessionStorage.setItem(contextName, jsonStr);
                window.sessionStorage.setItem(`${contextName}SetTime`, getTimestamp());
            }
        };

        // Keep call args to avoid duplicate calls
        const callArgs = useRef(undefined);

        // Either call the hook, or assign the function
        const request = requestHook ? requestHook(setIsLoading) : requestFunc;

        const _refreshInternal = useCallback(async (args) => {
            // If already being called with same args, don't run again
            if (status !== 'not started' && isEqual(args, callArgs.current) ) return;
            setState(prev => ({ ...prev, status: 'loading' }));

            try {
                callArgs.current = args;
                const next = await request(args);
                // If call args changed until the promise resolved it means that another call was made,
                // ignore the results of this one.
                setState(prev => ({ ...prev, data: next, status: 'complete' }));
                if (!isEqual(args, callArgs.current)) return;
                callArgs.current = undefined;
            } catch (error) {
                setState(prev => ({ ...prev, status: 'error' }));
                throw error;
            }
        }, [request]);

        const _refreshRef = useRef(null);

        // Wrap the internal refresh function so that it may only run once at a time.
        const refresh = useCallback((args) => {
            if (_refreshRef.current) return _refreshRef.current;
            return _refreshRef.current = _refreshInternal(args)
                .finally(() => _refreshRef.current = null);
        }, [_refreshInternal]);

        const reset = useCallback(() => {
            _refreshRef.current = null;
            setState(prev => ({ ...prev, status: 'complete' }));
        });

        const update = useCallback((next) => {
            const state = { data, status };
            setState(merge({}, state, next));
        });

        const contextValue = useMemo(() => ({
            data,
            status,
            refresh,
            reset,
            update
        }), [data, status]);

        return (
            <Context.Provider value={contextValue}>
                <LoadingScreen open={isLoading} />
                {children}
            </Context.Provider>
        );
    }

    function useDatabase() {
        return useContext(Context);
    }

    return { Provider: DatabaseManager, use: useDatabase };
}